import { useCallback, useEffect, useRef, useState } from "react";
import {
  submitPresetBakeJob,
  submitRegressionBake,
  pollBakeJob,
} from "@/services/nvgSimulationApi";
import type {
  ImageSize,
  NvgBakeState,
  NvgRendererHandle,
  SimulationPayload,
} from "@/types/nvgSimulation";

const POLL_INTERVAL_MS = 2000;
const TERMINAL_STATUSES = new Set([
  "complete",
  "complete_no_data",
  "failed",
  "error",
]);

export function useNvgBake(
  rendererRef: React.RefObject<NvgRendererHandle | null>,
) {
  const [state, setState] = useState<NvgBakeState>({
    phase: "idle",
    jobId: null,
    pollStatus: null,
    shaderParams: null,
    error: null,
  });

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeJobIdRef = useRef<string | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleJobComplete = useCallback(
    async (payload: SimulationPayload, jobId: string) => {
      if (activeJobIdRef.current !== jobId) return;

      setState((prev) => ({ ...prev, phase: "loading_textures" }));

      try {
        await rendererRef.current?.loadSimulation(payload);
        if (activeJobIdRef.current !== jobId) return;
        setState((prev) => ({
          ...prev,
          phase: "ready",
          shaderParams: payload.shaderParams,
        }));
      } catch (err) {
        if (activeJobIdRef.current !== jobId) return;
        setState((prev) => ({
          ...prev,
          phase: "error",
          error: err instanceof Error ? err.message : "Failed to load textures",
        }));
      }
    },
    [rendererRef],
  );

  const startPolling = useCallback(
    (jobId: string) => {
      stopPolling();

      pollIntervalRef.current = setInterval(async () => {
        if (activeJobIdRef.current !== jobId) {
          stopPolling();
          return;
        }

        try {
          const result = await pollBakeJob(jobId);

          if (activeJobIdRef.current !== jobId) return;

          setState((prev) => ({ ...prev, pollStatus: result.status }));

          if (TERMINAL_STATUSES.has(result.status)) {
            stopPolling();

            if (
              (result.status === "complete" ||
                result.status === "complete_no_data") &&
              result.assets &&
              result.shader_params
            ) {
              const payload: SimulationPayload = {
                assets: result.assets,
                shaderParams: result.shader_params,
                jobId,
              };
              await handleJobComplete(payload, jobId);
            } else {
              setState((prev) => ({
                ...prev,
                phase: "error",
                error: `Job ended with status: ${result.status}`,
              }));
            }
          }
        } catch (err) {
          if (activeJobIdRef.current !== jobId) return;
          stopPolling();
          setState((prev) => ({
            ...prev,
            phase: "error",
            error: err instanceof Error ? err.message : "Polling failed",
          }));
        }
      }, POLL_INTERVAL_MS);
    },
    [stopPolling, handleJobComplete],
  );

  const startJob = useCallback(
    async (jobId: string) => {
      activeJobIdRef.current = jobId;
      setState({
        phase: "polling",
        jobId,
        pollStatus: null,
        shaderParams: null,
        error: null,
      });
      startPolling(jobId);
    },
    [startPolling],
  );

  const submitPreset = useCallback(
    async (name: string, imageSize?: ImageSize) => {
      stopPolling();
      setState({
        phase: "submitting",
        jobId: null,
        pollStatus: null,
        shaderParams: null,
        error: null,
      });
      try {
        const { job_id } = await submitPresetBakeJob(name, imageSize);
        await startJob(job_id);
      } catch (err) {
        setState((prev) => ({
          ...prev,
          phase: "error",
          error: err instanceof Error ? err.message : "Submission failed",
        }));
      }
    },
    [stopPolling, startJob],
  );

  const submitRegression = useCallback(
    async (testCaseName: string, imageSize?: ImageSize) => {
      stopPolling();
      setState({
        phase: "submitting",
        jobId: null,
        pollStatus: null,
        shaderParams: null,
        error: null,
      });
      try {
        const { job_id } = await submitRegressionBake(testCaseName, imageSize);
        await startJob(job_id);
      } catch (err) {
        setState((prev) => ({
          ...prev,
          phase: "error",
          error: err instanceof Error ? err.message : "Submission failed",
        }));
      }
    },
    [stopPolling, startJob],
  );

  const reset = useCallback(() => {
    stopPolling();
    activeJobIdRef.current = null;
    rendererRef.current?.reset();
    setState({
      phase: "idle",
      jobId: null,
      pollStatus: null,
      shaderParams: null,
      error: null,
    });
  }, [stopPolling, rendererRef]);

  return { state, submitPreset, submitRegression, reset };
}
